import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
  MenuItem, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ChartBlock } from '../components/AnalysisChart';
import { MarkdownView } from '../components/MarkdownView';
import {
  AnalysisService, PromptService, type Analysis, type AnalysisContext,
  type AnalysisPreset, type FinancialProfile, type SavedPrompt,
} from '../services/analysis-service';

const RISK = ['conservative', 'balanced', 'growth', 'aggressive'];

export function FinancialAnalysisPage() {
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState<FinancialProfile>({});
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [selected, setSelected] = useState<Analysis | null>(null);
  const [ctx, setCtx] = useState<AnalysisContext | null>(null);
  const [prompt, setPrompt] = useState(
    'Do a full financial health check. Cover concentration risk, retirement readiness ' +
    'against my target age and spending, tax-efficient drawdown order across the ' +
    'registered and non-registered accounts, and anything that looks like it needs ' +
    'attention soon.',
  );
  const [presets, setPresets] = useState<AnalysisPreset[]>([]);
  const [saved, setSaved] = useState<SavedPrompt[]>([]);
  const [canSchedule, setCanSchedule] = useState(false);
  const [promptName, setPromptName] = useState('');
  const [schedule, setSchedule] = useState('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved2, setSaved2] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, l, pr] = await Promise.all([
        AnalysisService.getProfile(), AnalysisService.list(), PromptService.list(),
      ]);
      setProfile(p);
      setAnalyses(l.analyses);
      setPresets(pr.presets);
      setSaved(pr.saved);
      setCanSchedule(pr.scheduling_available);
    } catch {
      setError('Could not load analyses.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveProfile = async () => {
    setBusy(true); setError(''); setSaved2('');
    try {
      setProfile(await AnalysisService.saveProfile(profile));
      setSaved2('Profile saved. Future analyses start from these facts.');
    } catch {
      setError('Could not save the profile.');
    } finally { setBusy(false); }
  };

  const takeSnapshot = async () => {
    setBusy(true); setError('');
    try {
      setCtx(await AnalysisService.getContext());
    } catch {
      setError('Could not build the snapshot.');
    } finally { setBusy(false); }
  };

  const savePrompt = async () => {
    if (!promptName || !prompt) { setError('Give the prompt a name first.'); return; }
    setBusy(true); setError(''); setSaved2('');
    try {
      await PromptService.save({ name: promptName, prompt, schedule });
      setSaved2(schedule === 'none'
        ? 'Prompt saved. It will appear under Saved for one-click running.'
        : `Prompt saved and scheduled ${schedule}.`);
      setPromptName('');
      void load();
    } catch {
      setError('Could not save the prompt. Scheduling needs an AI key in Settings.');
    } finally { setBusy(false); }
  };

  const removePrompt = async (id: string) => {
    await PromptService.remove(id);
    void load();
  };

  const openAnalysis = async (id: string) => {
    setBusy(true);
    try {
      const full = await AnalysisService.get(id);
      setSelected(full.analysis);
    } catch {
      setError('Could not open that analysis.');
    } finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    await AnalysisService.remove(id);
    void load();
  };

  /** What to paste into Claude. The model reads the brief, writes the
   * analysis, and POSTs it back — so nothing is retyped by hand and the
   * result is stored against the exact snapshot it reasoned over. */
  const mcpInstructions = useMemo(() => {
    if (!ctx) return '';
    return [
      `Using the FolioWorth MCP server, GET /api/v1/analysis/context.`,
      ``,
      `Answer this question:`,
      prompt,
      ``,
      `Then POST the analysis to /api/v1/analyses with:`,
      `  prompt      the question above`,
      `  title       a short title`,
      `  source      "mcp"`,
      `  snapshot_id ${ctx.snapshot_id}`,
      `  result_md   your analysis as markdown`,
      `  charts      [{type,title,series:[{name,points:[{x,y}]}]}]`,
      ``,
      `If you learn a durable fact about the household, PUT it to`,
      `/api/v1/analysis/profile so the next analysis starts from it.`,
    ].join('\n');
  }, [ctx, prompt]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h4">Analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Ask a question about the portfolio. The answer is stored with the snapshot it was based on.
          </Typography>
        </Box>
        <IconButton onClick={() => void load()} aria-label="Refresh"><RefreshIcon /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved2 && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved2('')}>{saved2}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Run an analysis" />
        <Tab label={`Past analyses (${analyses.length})`} />
        <Tab label="What I remember" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="h6">Start from a question</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                One click fills the box below. Edit it freely — the prompt is stored with the result.
              </Typography>
              <Grid container spacing={1}>
                {presets.map((p) => (
                  <Grid item xs={12} sm={6} md={4} key={p.key}>
                    <Paper
                      variant="outlined"
                      onClick={() => { setPrompt(p.prompt); setPromptName(p.name); }}
                      sx={{ p: 1.5, height: '100%', cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="subtitle2">{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {saved.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Saved</Typography>
                <Stack spacing={1}>
                  {saved.map((sp) => (
                    <Paper key={sp.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box sx={{ minWidth: 0, cursor: 'pointer' }}
                          onClick={() => { setPrompt(sp.prompt); setPromptName(sp.name); }}>
                          <Typography variant="subtitle2" noWrap>{sp.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {sp.schedule === 'none' ? 'Run on demand' : `Runs ${sp.schedule}`}
                            {sp.next_run_at ? ` · next ${new Date(sp.next_run_at).toLocaleDateString()}` : ''}
                            {sp.last_error ? ` · last run failed` : ''}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {sp.schedule !== 'none' && (
                            <Tooltip title={sp.last_error
                              ? `Last scheduled run failed: ${sp.last_error}`
                              : 'Runs on the server against your own AI key'}>
                              <Chip size="small" label={sp.schedule}
                                color={sp.last_error ? 'warning' : 'default'} />
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={() => void removePrompt(sp.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>The question</Typography>
              <TextField
                fullWidth multiline minRows={4} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                helperText="The same portfolio answers a drawdown question differently from a concentration one, so the prompt is kept with the result."
              />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Button variant="contained" onClick={() => void takeSnapshot()} disabled={busy}>
                  {busy ? <CircularProgress size={20} /> : 'Take snapshot'}
                </Button>
                <TextField size="small" label="Save as" value={promptName}
                  onChange={(e) => setPromptName(e.target.value)} sx={{ minWidth: 200 }} />
                <TextField
                  select size="small" label="Schedule" value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  sx={{ minWidth: 150 }}
                  disabled={!canSchedule}
                  helperText={canSchedule ? '' : 'Needs an AI key in Settings'}
                >
                  <MenuItem value="none">Run on demand</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                </TextField>
                <Button onClick={() => void savePrompt()} disabled={busy}>Save prompt</Button>
              </Stack>
            </CardContent>
          </Card>

          {ctx && (
            <>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Snapshot</Typography>
                    <Chip size="small" label={new Date(ctx.snapshot.taken_at).toLocaleString()} />
                  </Stack>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Stat label="Net worth" value={fmt(ctx.snapshot.net_worth, ctx.snapshot.base_currency)}
                      help="Every account's balance in the household's base currency, derived from ledger entries." />
                    <Stat label="Accounts" value={String(ctx.snapshot.accounts?.length ?? 0)}
                      help="Accounts included in this snapshot." />
                    <Stat label="Positions" value={String(ctx.snapshot.positions?.length ?? 0)}
                      help="Holdings as the brokerage reported them. Pooled by symbol when concentration is measured." />
                    <Stat label="Trailing 12m income" value={fmt(ctx.snapshot.trailing_12m_income, ctx.snapshot.base_currency)}
                      help="Dividends, interest and distributions received in the last 12 months, net of withholding tax." />
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="h6">Run it in Claude</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Uses your Claude subscription through the MCP server — no API key, no token charges.
                      </Typography>
                    </Box>
                    <Tooltip title="Copy">
                      <IconButton onClick={() => void navigator.clipboard.writeText(mcpInstructions)}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography component="pre" variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}>
                      {mcpInstructions}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Brief the model reads</Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                    <Typography component="pre" variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}>
                      {ctx.brief}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={1}>
          {analyses.length === 0 && (
            <Alert severity="info">No analyses yet. Take a snapshot and run one.</Alert>
          )}
          {analyses.map((a) => (
            <Card key={a.id}>
              <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Link
                    to="/analysis/$id"
                    params={{ id: a.id }}
                    style={{ minWidth: 0, textDecoration: 'none', color: 'inherit' }}
                  >
                    <Typography variant="subtitle1" noWrap>{a.title || 'Untitled analysis'}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{a.prompt}</Typography>
                  </Link>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={a.source === 'mcp' ? 'Claude (MCP)' : a.source === 'planner' ? 'Household planner' : 'LLM key'} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.created_at).toLocaleDateString()}
                    </Typography>
                    <IconButton size="small" onClick={() => void remove(a.id)} aria-label="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>What I remember about you</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Stated once and reused, so the next analysis does not begin by asking again.
              Leaving a field blank keeps whatever is already stored.
            </Typography>
            <Grid container spacing={2}>
              <Field label="Birth year" type="number" value={profile.birth_year ?? ''}
                onChange={(v) => setProfile({ ...profile, birth_year: v ? Number(v) : null })} />
              <Field label="Target retirement age" type="number" value={profile.retirement_age ?? ''}
                onChange={(v) => setProfile({ ...profile, retirement_age: v ? Number(v) : null })} />
              <Field label="Annual spend (today's dollars)" value={profile.annual_spend ?? ''}
                onChange={(v) => setProfile({ ...profile, annual_spend: v })} />
              <Grid item xs={12} sm={6} md={3}>
                <TextField select fullWidth label="Risk tolerance" value={profile.risk_tolerance ?? ''}
                  onChange={(e) => setProfile({ ...profile, risk_tolerance: e.target.value })}>
                  {RISK.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Field label="Province" value={profile.province ?? ''}
                onChange={(v) => setProfile({ ...profile, province: v.toUpperCase().slice(0, 2) })} />
              <Field label="Dependants" type="number" value={profile.dependants ?? ''}
                onChange={(v) => setProfile({ ...profile, dependants: v ? Number(v) : null })} />
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Anything else that matters"
                  placeholder="A pension, an expected inheritance, positions never to sell…"
                  value={profile.notes ?? ''}
                  onChange={(e) => setProfile({ ...profile, notes: e.target.value })} />
              </Grid>
            </Grid>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => void saveProfile()} disabled={busy}>
              Save
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selected?.title || 'Analysis'}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <>
              <Typography variant="caption" color="text.secondary">
                {new Date(selected.created_at).toLocaleString()}
                {selected.model ? ` · ${selected.model}` : ''}
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, my: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2"><strong>Asked:</strong> {selected.prompt}</Typography>
              </Paper>
              <MarkdownView text={selected.result_md} />
              {(selected.charts ?? []).length > 0 && <Divider sx={{ my: 2 }} />}
              {(selected.charts ?? []).map((c, i) => <ChartBlock key={i} spec={c} />)}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <Grid item xs={6} md={3}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {help && (
          <Tooltip title={help} arrow enterTouchDelay={0}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="h6">{value}</Typography>
    </Grid>
  );
}

function Field({ label, value, onChange, type }: {
  label: string; value: string | number; onChange: (v: string) => void; type?: string;
}) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <TextField fullWidth label={label} type={type} value={value}
        onChange={(e) => onChange(e.target.value)} />
    </Grid>
  );
}

function fmt(v: string | undefined, currency: string) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return String(v ?? '');
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: currency || 'CAD', maximumFractionDigits: 0,
  }).format(n);
}
